<template>
    <div
        class="group my-4 w-full rounded-lg border border-iron-200 bg-white px-4 py-5 shadow-sm transition-all duration-200 hover:shadow-md"
    >
        <div
            class="items-center justify-between space-x-3 border-b pb-4 sm:flex"
        >
            <div class="flex items-center gap-2">
                <div class="flex-shrink-0"></div>
                <div class="flex min-w-0 gap-3">
                    <p class="font-medium text-iron-900">
                        {{ team.name }}
                    </p>
                </div>
            </div>

            <div
                v-if="user.id === team.owner.id"
                class="mt-3 flex flex-shrink-0 justify-start self-center sm:mt-0"
            >
                <div class="inline-flex rounded-md shadow-sm">
                    <InviteToTeam :team="team">
                        <AddButton data-cy="add-day-off">
                            {{ $t("teams.invite") }}</AddButton
                        >
                    </InviteToTeam>
                    <a
                        :href="route('app.teams.billing', { team: team.id })"
                        class="relative ml-2 inline-flex items-center gap-2 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                    >
                        <CreditCard class="h-[20px] w-[20px]" />
                        <span class="hidden sm:inline-block">{{
                            $t("teams.billing")
                        }}</span>
                    </a>

                    <button
                        :disabled="loading"
                        class="relative ml-2 inline-flex items-center gap-2 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        @click="deleteTeam"
                    >
                        <trash-icon class="h-[20px] w-[20px]" />
                        <span class="hidden sm:inline-block">{{
                            $t("teams.delete")
                        }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div>
            <ul role="list" class="divide-y divide-iron-100">
                <li class="flex items-center justify-between gap-x-6 py-5">
                    <div class="flex w-full justify-between">
                        <div class="flex min-w-0 items-center gap-x-4">
                            <img
                                v-if="team.owner.profile_photo_path"
                                class="h-12 w-12 flex-none rounded-full bg-iron-50"
                                :src="
                                    'https://zaptime.s3.eu-central-1.amazonaws.com/' +
                                    team.owner.profile_photo_path
                                "
                                alt=""
                            />
                            <div
                                v-else
                                class="leading-12 flex h-12 w-12 items-center justify-center rounded-full bg-orange-200 text-center text-xl font-semibold text-white"
                            >
                                <span>
                                    {{ getTwoInitials(team.owner.name) }}
                                </span>
                            </div>
                            <div class="min-w-0 flex-auto">
                                <p
                                    class="text-sm font-semibold leading-6 text-iron-900"
                                >
                                    {{ team.owner.name }}
                                    <span
                                        class="ml-1 rounded-full bg-z-primary-600 p-1 px-3 text-xs font-semibold uppercase tracking-tighter text-white"
                                        >{{ $t("teams.owner") }}</span
                                    >
                                </p>
                                <p
                                    class="mt-1 truncate text-xs leading-5 text-iron-500"
                                >
                                    {{ team.owner.email }}
                                </p>
                            </div>
                        </div>
                    </div>
                </li>
                <li
                    v-for="teamUser in team.users"
                    :key="teamUser.id"
                    class="flex items-center justify-between gap-x-6 py-5"
                >
                    <div class="flex w-full justify-between">
                        <div class="flex min-w-0 items-center gap-x-4">
                            <img
                                v-if="teamUser.profile_photo_path"
                                class="h-12 w-12 flex-none rounded-full bg-iron-50"
                                :src="
                                    'https://zaptime.s3.eu-central-1.amazonaws.com/' +
                                    teamUser.profile_photo_path
                                "
                                alt=""
                            />
                            <div
                                v-else
                                class="leading-12 flex h-12 w-12 items-center justify-center rounded-full bg-orange-200 text-center text-xl font-semibold text-white"
                            >
                                <span>
                                    {{ getTwoInitials(teamUser.name) }}
                                </span>
                            </div>
                            <div class="min-w-0 flex-auto">
                                <p
                                    class="text-sm font-semibold leading-6 text-iron-900"
                                >
                                    {{ teamUser.name }}
                                    <span
                                        class="ml-1 rounded-full bg-z-primary-600 p-1 px-3 text-xs font-semibold uppercase tracking-tighter text-white"
                                        >{{
                                            $t(
                                                "teams." +
                                                    teamUser.membership.role,
                                            )
                                        }}</span
                                    >
                                </p>
                                <p
                                    class="mt-1 truncate text-xs leading-5 text-iron-500"
                                >
                                    {{ teamUser.email }}
                                </p>
                            </div>
                        </div>

                        <div class="flex gap-2">
                            <UpdateRole
                                v-if="
                                    teamUser.id !== user.id &&
                                    user.id === team.owner.id
                                "
                                :team="team"
                                :team-user="teamUser"
                            >
                                <Button variant="outline" size="sm">
                                    {{ $t("teams.update_role") }}
                                </Button>
                            </UpdateRole>

                            <Button
                                v-if="
                                    teamUser.id !== user.id &&
                                    user.id === team.owner.id
                                "
                                variant="outline"
                                size="sm"
                                @click="removeMember(teamUser.id)"
                            >
                                {{ $t("teams.remove_member") }}
                            </Button>
                        </div>

                        <Button
                            v-if="user.id === teamUser.id"
                            variant="outline"
                            size="sm"
                            @click="leaveTeam()"
                        >
                            {{ $t("teams.leave") }}
                        </Button>
                    </div>
                </li>

                <li
                    v-for="teamInvitation in team.team_invitations"
                    :key="teamInvitation.id"
                    class="flex items-center justify-between gap-x-6 py-5"
                >
                    <div class="flex w-full justify-between">
                        <div class="flex min-w-0 items-center gap-x-4">
                            <div class="min-w-0 flex-auto">
                                <p
                                    class="mt-1 truncate text-xs leading-5 text-iron-500"
                                >
                                    {{ teamInvitation.email }}
                                    <span
                                        class="ml-1 rounded-full bg-z-primary-200 p-1 px-3 text-xs font-semibold uppercase tracking-tighter text-z-primary-600"
                                        >{{ $t("teams.pending") }}</span
                                    >
                                </p>
                            </div>
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            @click="deleteInvitation(teamInvitation.id)"
                        >
                            {{ $t("teams.cancel_invitation") }}
                        </Button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/24/outline";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import route from "ziggy-js";
import InviteToTeam from "@/Pages/ZapTime/App/Teams/components/InviteToTeam.vue";
import AddButton from "../../Atomic/AddButton.vue";
import UpdateRole from "@/Pages/ZapTime/App/Teams/components/UpdateRole.vue";
const { show } = useConfirm();
const { t } = useI18n();
import { CreditCard } from "lucide-vue-next";

const props = defineProps({
    team: {
        required: true,
        type: Object,
    },
});

const page = usePage();
const loading = ref(false);
const user = computed((): any => page.props.user);

const deleteTeam = async function () {
    const result = await show({
        header: t("teams.delete_confirmation.are_you_sure"),
        message: t("teams.delete_confirmation.description"),
        confirm: t("teams.delete"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("app.teams.delete", {
            team: props.team.id,
        }),
    );
};

function getTwoInitials(name: string) {
    const words = name.trim().split(" ");
    // Ensure there's at least one word to avoid errors
    if (words.length === 1) {
        return words[0][0] + (words[0][1] ? words[0][1] : "");
    } else {
        return words[0][0] + words[words.length - 1][0];
    }
}

async function removeMember(userId: number) {
    const result = await show({
        header: t("teams.remove_member_confirmation.are_you_sure"),
        message: t("teams.remove_member_confirmation.description"),
        confirm: t("teams.remove_member"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-members.destroy", {
            user: userId,
            team: props.team.id,
        }),
    );
}

async function leaveTeam() {
    const result = await show({
        header: t("teams.leave_confirmation.are_you_sure"),
        message: t("teams.leave_confirmation.description"),
        confirm: t("teams.leave"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-members.destroy", {
            user: user.value.id,
            team: props.team.id,
        }),
    );
}

async function deleteInvitation(invitationId: number) {
    const result = await show({
        header: t("teams.leave_confirmation.are_you_sure"),
        message: t("teams.leave_confirmation.description"),
        confirm: t("teams.leave"),
        cancel: t("cancel"),
    });

    if (!result) {
        return;
    }

    router.delete(
        route("team-invitations.destroy", {
            invitation: invitationId,
        }),
    );
}
</script>
